import { Fragment, useState } from 'react'
import { Betrag } from './Betrag'
import { Abrufausfall, useAbruf } from './Abruf'
import { api, betrag as alsBetrag, periodenkuerzel } from './api'
import { Spaltenleiste, useSpaltenbreiten } from './Spaltenbreiten'
import { gesellschaftsname, kontobezeichnung, t } from './sprache'
import type { Sprachschluessel } from './sprache'
import type { Icabstimmblatt, Icgesellschaft, Icwert } from './typen'

/**
 * Die IC-Abstimmung — **sie zeigt, sie bucht nicht** (2.10 Abschnitt 9, R827).
 *
 * **Der Auftrag des Auftraggebers vom 2026-09-27:** *„Die IC-Abstimmung dient
 * rein dem Aufzeigen der paarweisen Konsolidierungsdifferenzen. Es wird nichts
 * gebucht. […] Ziel ist es auf einen Blick die Gesamtdifferenz je Pärchen zu
 * sehen und diese bei Bedarf aufzuklappen um folgende Frage zu beantworten:
 * ‚Wer hat mit wem in welcher Periode auf welchen Konten welche Werte und zu
 * welchem Differenzbetrag führt das?'"*
 *
 * **Die ganze Frage in einem Bild:** *wer* und *mit wem* stehen in der Zeile
 * des Pärchens, *in welcher Periode* in der Spalte, *auf welchem Konto* in der
 * aufgeklappten Zeile, *welche Werte* in der Zelle — und *welcher
 * Differenzbetrag* in der Zeile darüber.
 *
 * **Die Farbe trägt ihr Wort** (R143, R828): Grün und Rot allein wären für
 * jeden zehnten Mann keine Auskunft und im Ausdruck gar keine. Was gilt, steht
 * als Beschriftung an der Zelle, und die Schwelle steht über dem Blatt.
 */

/** Die beiden Blätter (R827). */
const BLAETTER = ['schuldenkonsolidierung', 'aufwandertragkonsolidierung'] as const

type Blatt = (typeof BLAETTER)[number]

/** Was in der Zelle steht — die Schaltfläche stellt um, sie lädt nicht nach. */
type Sicht = 'saldo' | 'verkehr'

/**
 * Wie eine Differenz dasteht (R828).
 *
 * **Genau null ist eine eigene Auskunft** und nicht nur eine kleine Differenz:
 * Dort steht ein Häkchen statt der Null.
 */
export function befund(wert: number, schwelle: number): 'stimmt' | 'innerhalb' | 'ausserhalb' {
  if (wert === 0) {
    return 'stimmt'
  }

  return Math.abs(wert) <= schwelle ? 'innerhalb' : 'ausserhalb'
}

/** Der Wert, den die gewählte Sicht zeigt. */
/**
 * Die echte Differenz — die ganze ohne ihren Währungsanteil (2.9 Abschnitt
 * 13.8, R1481). Ohne Wert bleibt die Zelle leer.
 */
export function echt(gesamt: number | null, waehrung: number | null): number | null {
  return gesamt === null ? null : gesamt - (waehrung ?? 0)
}

export function ausSicht(wert: Icwert | undefined, sicht: Sicht): number | null {
  if (wert === undefined) {
    return null
  }

  return sicht === 'saldo' ? wert.saldo : wert.verkehr
}

/**
 * Eine Zelle mit Differenz — Farbe, Wort und Zahl.
 *
 * **Sie steht im Modul und nicht im Rumpf von `Icabstimmung`** (Befund vom
 * 2026-09-29: *„wenn ich auf aufklappen drücke, friert der browser ein"*).
 *
 * Eine Komponente, die im Rumpf einer anderen steht, wird bei **jedem**
 * Durchgang neu erzeugt. React vergleicht Komponenten an ihrer Identität: Eine
 * neue Funktion ist ein neues Bauteil, also wirft es den alten Teilbaum weg
 * und baut ihn auf, statt ihn zu ändern. Zwölf Zellen je Zeile werden so bei
 * jedem Tastendruck abgerissen und neu gesetzt.
 *
 * Das war lange nur Verschwendung. Seit an dieser Tabelle der Spaltenzug
 * hängt, ist es ein Stillstand: Der Zug beobachtet die Tabelle, um seine
 * gemessene Breite zu verwerfen, wenn sich etwas ändert (R487) — und hier
 * änderte sich fortwährend etwas. Verwerfen, messen, neu zeichnen, verwerfen:
 * In 700 Millisekunden wurde die Spaltenleiste **1186 mal** ein- und
 * ausgehängt, und der Browser kam nicht mehr zurück.
 */
function Zelle({ wert, schwelle }: { wert: number | null; schwelle: number }) {
  if (wert === null) {
    // **Leer heißt: hier war nichts.** Eine Null wäre eine Aussage, und
    // „nichts gebucht" ist eine andere als „hebt sich auf".
    return <td className="zahl leer" />
  }

  const art = befund(wert, schwelle)

  return (
    <td className={`zahl icdifferenz ${art}`} title={t(`ic.befund.${art}` as Sprachschluessel)}>
      {art === 'stimmt' ? (
        <span aria-label={t('ic.befund.stimmt')}>✓</span>
      ) : (
        <span aria-label={`${alsBetrag(wert)} · ${t(`ic.befund.${art}` as Sprachschluessel)}`}>
          <Betrag wert={wert} />
        </span>
      )}
    </td>
  )
}

/**
 * Eine Gesellschaft mit ihren Konten — **erst die Gesellschaft, dann ihre
 * Konten** (Auftrag vom 2026-09-27). Bis dahin war es umgekehrt: eine Zeile je
 * Konto mit dem Namen der Gesellschaft dahinter. Die Frage des Aufrisses
 * lautet aber „wer hat was gemeldet" — und wer, gehört damit oben hin.
 *
 * Im Modul und nicht im Rumpf, aus demselben Grund wie `Zelle`. Dieselben
 * Zeilen stehen unter einem Pärchen und unter „Ohne Partner" (R929).
 */
function Gesellschaftszeilen({ g, perioden, sicht }: { g: Icgesellschaft; perioden: number[]; sicht: Sicht }) {
  return (
    <>
      <tr className="icgesellschaft">
        <td>{gesellschaftsname(g.einheit)}</td>
        {perioden.map((p, i) => {
          const wert = ausSicht(g.werte[i], sicht)
          return (
            <td key={p} className="zahl">
              {wert !== null && <Betrag wert={wert} />}
            </td>
          )
        })}
      </tr>

      {g.konten.map((k) => (
        <tr key={k.konto} className="ickonto">
          {/*
            **Die Kontozeile trägt ihren Namen, sonst nichts** (Auftrag vom
            2026-09-29: *„Bitte entferne die umrandeten Worte Forderung und
            Verbindlichkeit. Das ist für den Anwender nicht wichtig, da die
            Konten entsprechend bekannt sind"*).

            Davor stand an jeder Kontozeile eine Marke mit *Forderung* oder
            *Verbindlichkeit*. Wer ein Abstimmblatt liest, kennt seine
            Konten — „1450 Forderungen gegen verbundene Unternehmen" sagt
            beides schon selbst, und die Marke sagte es ein zweites Mal. Das
            Vorzeichen daneben sagt es ein drittes Mal.
          */}
          <td>{kontobezeichnung(k.konto)}</td>
          {perioden.map((p, i) => {
            const wert = ausSicht(k.werte[i], sicht)
            return (
              <td key={p} className="zahl gedaempft">
                {wert !== null && <Betrag wert={wert} />}
              </td>
            )
          })}
        </tr>
      ))}
    </>
  )
}

export function Icabstimmung({ abschluss }: { abschluss: string }) {
  const [blatt, setBlatt] = useState<Blatt>('schuldenkonsolidierung')
  const [sicht, setSicht] = useState<Sicht>('saldo')
  /** Welche Pärchen aufgeklappt sind — mehrere dürfen es sein. */
  const [offen, setOffen] = useState<ReadonlySet<string>>(new Set())

  /*
    **Die Spalten werden gezogen** (7.3 Abschnitt 10, R415, R416 — Auftrag vom
    2026-09-29). Die Paarspalte für sich, alle Periodenspalten gemeinsam: Zwölf
    Monate tragen Beträge derselben Größenordnung in derselben Währung, und wer
    den September breiter zieht, meint nicht den September, sondern seine
    Zahlen. Je Blatt eine eigene Breite — die Schuldenkonsolidierung und die
    Aufwands- und Ertragskonsolidierung sind zwei Bilder.
  */
  const { breiten, rahmen } = useSpaltenbreiten(`ic:${blatt}`)

  /*
    **Das Blatt ist ein Hauptabruf** (7.2 Abschnitt 39, R1211): Kam es nicht,
    steht „Ließ sich nicht laden" da und kein leeres Blatt (R1210).
  */
  const abruf = useAbruf<Icabstimmblatt | null>(
    abschluss === '' ? null : () => api.icabstimmung(abschluss, blatt),
    [abschluss, blatt],
  )
  const satz = abruf.wert
  const laedt = abruf.laedt

  function klappe(schluessel: string) {
    setOffen((bisher) => {
      const neu = new Set(bisher)
      if (neu.has(schluessel)) {
        neu.delete(schluessel)
      } else {
        neu.add(schluessel)
      }
      return neu
    })
  }

  /** Die Beträge ohne Partner, je Gesellschaft (R929). */
  const ohnePartner = satz?.ohnePartner ?? []

  return (
    <section className="icabstimmung">
      {/*
        **Sie bucht nicht, und das steht da** (R827): Wer ein Blatt mit roten
        Zahlen sieht, fragt als erstes, was damit geschieht.
      */}
      <p className="erlaeuterung">{t('ic.zeigtnur')}</p>

      <div className="kopfzeile">
        {/*
          **Zwei Blätter** (R827) — die Schuldenkonsolidierung stimmt
          Forderungen gegen Verbindlichkeiten ab, die Aufwands- und
          Ertragskonsolidierung Erträge gegen Aufwendungen.
        */}
        <div className="reiterleiste">
          {BLAETTER.map((b) => (
            <button
              key={b}
              type="button"
              className={blatt === b ? 'reiter aktiv' : 'reiter'}
              aria-pressed={blatt === b}
              onClick={() => setBlatt(b)}
            >
              {t(`ic.blatt.${b}` as Sprachschluessel)}
            </button>
          ))}
        </div>

        {/*
          **Die Sicht ist die halbe Diagnose** (2.10 Abschnitt 9.4): Eine
          Differenz, die im Saldo steht und in keiner Verkehrszahl, wird aus
          einer früheren Periode mitgeschleppt.
        */}
        <div className="sichtwahl">
          <button
            type="button"
            className={sicht === 'saldo' ? 'reiter aktiv' : 'reiter'}
            aria-pressed={sicht === 'saldo'}
            onClick={() => setSicht('saldo')}
          >
            {satz?.istBestand === false ? t('ic.sicht.ytd') : t('ic.sicht.saldo')}
          </button>
          <button
            type="button"
            className={sicht === 'verkehr' ? 'reiter aktiv' : 'reiter'}
            aria-pressed={sicht === 'verkehr'}
            onClick={() => setSicht('verkehr')}
          >
            {t('ic.sicht.verkehr')}
          </button>
        </div>
      </div>

      {laedt && <p className="leerhinweis">{t('ic.laedt')}</p>}

      {abruf.ausgefallen && <Abrufausfall abruf={abruf} />}

      {!laedt && satz !== null && satz.leergrund !== null && (
        <p className="leerhinweis">{t(`ic.leer.${satz.leergrund}` as Sprachschluessel)}</p>
      )}

      {!laedt && satz !== null && satz.leergrund === null && (
        <>
          {/*
            **Die Schwelle steht über dem Blatt** (R828): Grün und Rot allein
            sagen nicht, ab wann rot beginnt.
          */}
          <p className="erlaeuterung">
            {t('ic.schwelle', { betrag: alsBetrag(satz.schwelle), waehrung: satz.waehrung })}
          </p>

          {satz.paare.length === 0 && ohnePartner.length === 0 ? (
            <p className="leerhinweis">{t('ic.leer.ohne-paare')}</p>
          ) : (
            <div className="tabellenrahmen" ref={rahmen}>
              <table className="icblatt positionstabelle">
                {/*
                  Die Leiste steht immer da — gezogen oder gemessen (R487).
                  Ohne sie rechnete der Browser jede Periodenspalte aus ihrem
                  eigenen Inhalt, und der Blick spränge beim Vergleich zweier
                  Monate über eine Stufe.
                */}
                <Spaltenleiste breiten={breiten} spalten={1 + satz.perioden.length} />
                <thead>
                  <tr>
                    <th className="positionsspalte">{t('ic.spalte.paar')}</th>
                    {satz.perioden.map((p) => (
                      <th key={p} className="zahl">
                        {periodenkuerzel(p)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {satz.paare.map((paar) => {
                    const schluessel = `${paar.einheitA}|${paar.einheitB}`
                    const auf = offen.has(schluessel)

                    return (
                      <Fragment key={schluessel}>
                        <tr className="icpaar">
                          <td>
                            <button
                              type="button"
                              className="klapper"
                              aria-expanded={auf}
                              onClick={() => klappe(schluessel)}
                            >
                              {auf ? '▾' : '▸'}
                            </button>
                            {gesellschaftsname(paar.einheitA)}
                            {' ↔ '}
                            {gesellschaftsname(paar.einheitB)}
                            {/*
                              **Eine abweichende Schwelle muß man sehen**
                              (R828): Sonst stünde eine Zahl grün da, die
                              zwei Zeilen tiefer rot wäre, und der Bildschirm
                              sagte nicht warum.
                            */}
                            {paar.eigeneSchwelle && (
                              <span
                                className="marke-abweichung"
                                title={t('ic.eigeneschwelle.titel')}
                              >
                                {t('ic.eigeneschwelle', {
                                  betrag: alsBetrag(paar.schwelle),
                                })}
                              </span>
                            )}
                          </td>
                          {/*
                            **Gegen die Schwelle zählt nur die echte
                            Differenz** (2.9 Abschnitt 13.8, R1483); der
                            Währungsanteil steht in der Zeile darunter.
                          */}
                          {satz.perioden.map((p, i) => (
                            <Zelle
                              key={p}
                              wert={echt(ausSicht(paar.werte[i], sicht), ausSicht(paar.waehrung?.[i], sicht))}
                              schwelle={paar.schwelle}
                            />
                          ))}
                        </tr>

                        {(paar.waehrung ?? []).some((w) => w.saldo !== 0 || w.verkehr !== 0) && (
                          <tr className="icwaehrung">
                            <td className="gedaempft">{t('ic.waehrung')}</td>
                            {satz.perioden.map((p, i) => {
                              const wert = ausSicht(paar.waehrung?.[i], sicht)
                              return (
                                <td key={p} className="zahl gedaempft">
                                  {wert !== null && <Betrag wert={wert} leerBeiNull />}
                                </td>
                              )
                            })}
                          </tr>
                        )}

                        {/*
                          **Erst die Gesellschaft, dann ihre Konten** (Auftrag
                          vom 2026-09-27). Bis dahin war es umgekehrt: eine
                          Zeile je Konto mit dem Namen der Gesellschaft
                          dahinter. Die Frage des Aufrisses lautet aber „wer
                          hat was gemeldet" — und wer, gehört damit oben hin.
                        */}
                        {auf &&
                          paar.gesellschaften.map((g) => (
                            <Gesellschaftszeilen key={g.einheit} g={g} perioden={satz.perioden} sicht={sicht} />
                          ))}
                      </Fragment>
                    )
                  })}

                  {/*
                    **Ohne Partner gibt es kein Pärchen** (5.1 Abschnitt 3.38,
                    R929): Der Betrag hat keine Gegenseite und deshalb keine
                    Differenz. Er steht je Gesellschaft unter den Pärchen —
                    immer aufgeklappt, weil er die Auskunft selbst ist und
                    nicht ihr Aufriss.
                  */}
                  {ohnePartner.length > 0 && (
                    <>
                      <tr className="icohnepartner">
                        <td colSpan={1 + satz.perioden.length}>{t('ic.ohnepartner')}</td>
                      </tr>
                      {ohnePartner.map((g) => (
                        <Gesellschaftszeilen key={g.einheit} g={g} perioden={satz.perioden} sicht={sicht} />
                      ))}
                    </>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </section>
  )
}
